import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { SUPPORTED_LANGS } from "../i18n";

export default function LanguageSwitcher({ variant = "topbar" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current =
    SUPPORTED_LANGS.find((l) => l.code === i18n.language) ||
    SUPPORTED_LANGS.find((l) => i18n.language?.startsWith(l.code)) ||
    SUPPORTED_LANGS[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("chizy_lang", code);
      document.documentElement.lang = code;
    } catch {
      /* localStorage may be unavailable */
    }
    setOpen(false);
  };

  const triggerClass =
    variant === "login"
      ? "px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white border border-white/15 rounded-lg flex items-center gap-2 text-xs font-bold backdrop-blur"
      : "h-10 px-2.5 hover:bg-slate-100 rounded-xl flex items-center gap-1.5 text-slate-700 text-xs font-bold border border-slate-200 bg-white";

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        data-testid="language-switcher-trigger"
        onClick={() => setOpen((v) => !v)}
        className={triggerClass}
        title={t("lang.switchTo")}
      >
        <Globe className="w-4 h-4" strokeWidth={2.2} />
        <span className="uppercase tracking-wider">{current.code}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            data-testid="language-switcher-menu"
            className="absolute right-0 mt-2 z-50 w-44 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
          >
            <div className="px-3 py-2 border-b border-slate-100 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              {t("lang.name")}
            </div>
            {SUPPORTED_LANGS.map((l) => {
              const active = current.code === l.code;
              return (
                <button
                  key={l.code}
                  data-testid={`language-option-${l.code}`}
                  onClick={() => change(l.code)}
                  className={`w-full text-left flex items-center gap-2.5 px-3 py-2.5 hover:bg-slate-50 transition ${
                    active ? "bg-[#FFFBE6]" : ""
                  }`}
                >
                  <span className="w-7 h-5 rounded bg-slate-900 text-[#FFDF00] flex items-center justify-center text-[9px] font-extrabold tracking-wider">
                    {l.flag}
                  </span>
                  <span className="text-sm font-semibold text-[#1A1A1A] flex-1">
                    {l.label}
                  </span>
                  {active && (
                    <Check
                      className="w-3.5 h-3.5 text-emerald-600"
                      strokeWidth={3}
                    />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
